import React, { useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { confirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import moment from 'moment';

import { generateFullName } from 'utils/generateFullNameUtils';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useLeadIntake } from 'modules/lead/hooks/useLeadIntake';
import { dateFormatMap } from 'constants/dropdownOptions';
import constants from 'constants/index';
import { generateFormPdf, getPropByString } from 'utils/utils';

const formatValue = ({ value, dateFormat, type, time, period, _practice_area_ }) => {
  let displayValue = '-';
  if (type === 'practice_area') {
    return _practice_area_ || value || '-';
  } else if (type === 'time') {
    if (time && period) {
      displayValue = `${time} ${period}`;
    }
  } else if (Array.isArray(value)) {
    displayValue = value?.join(', ').replace(/_/g, ' ');
  } else if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (Boolean(dateFormat) || type === 'Date') {
    displayValue = value ? moment(value).format(dateFormatMap.get(dateFormat) ?? 'MM/DD/YYYY') : '-';
  } else if ((typeof value === 'number' || typeof value === 'string') && value !== '') {
    displayValue = String(value);
  }
  return displayValue;
};

export default function Table() {
  const [expandedRows, setExpandedRows] = useState(null);
  const userContext = useUserDetailsContext();

  // Use the hook with lead_type='online' for online forms
  const { leadForms, paginatorTemplate, pageSize, acceptIntake, rejectIntake } = useLeadIntake(10, 'online');

  const approveIntake = (data) => {
    confirmDialog({
      message: 'Are you sure you want to create this lead as a draft?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        acceptIntake(data);
      },
      reject: () => null,
    });
  };

  const expandedRowTemplate = (data) => {
    const otherFields = data?.form_data?.filter((v) => !(v?.res_type === 'custom-field' || v?.field_type === 'contact'));
    return (
      <div className="card-bg p-3">
        <h5 className="sub-title">Contact Data</h5>
        <div className="dynamic-labels-container">
          <div className="label-value-pair">
            <span className="p-medium">Name</span>
            <span>{generateFullName(data?.contact_data)}</span>
          </div>
          {data?.contact_data?.dob && (
            <div className="label-value-pair">
              <span className="p-medium">Date of Birth</span>
              <span>{moment.parseZone(data?.contact_data?.dob).format(constants.month_date_year_format)}</span>
            </div>
          )}
          {data?.contact_data?.email && (
            <div className="label-value-pair">
              <span className="p-medium">Email</span>
              <span>{data?.contact_data?.email}</span>
            </div>
          )}
          {data?.contact_data?.phone && (
            <div className="label-value-pair">
              <span className="p-medium">Phone</span>
              <span>{data?.contact_data?.phone}</span>
            </div>
          )}
          {data?.contact_data?.additional_phone && (
            <div className="label-value-pair">
              <span className="p-medium">Additional Phone</span>
              <span>{data?.contact_data?.additional_phone}</span>
            </div>
          )}
          {data?.form_data
            ?.filter((v) => v?.res_type === 'custom-field')
            ?.map((item, index) => (
              <div key={index} className="label-value-pair">
                <span className="p-medium">{item?.label || item?.name}:</span>
                <span>{formatValue(item)}</span>
              </div>
            ))}
        </div>
        {otherFields?.length > 0 && (
          <>
            <h5 className="sub-title mt-3">Other fields</h5>
            <div className="dynamic-labels-container">
              {otherFields?.map((item, index) => (
                <div key={index} className="label-value-pair">
                  <span className="p-medium">{item?.label || item?.name}:</span>
                  <span>{formatValue(item)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const nameTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Name</span>
        <div className="d-inline-table">{generateFullName(rowData?.contact_data)}</div>
      </>
    );
  };

  const actionBody = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Action</span>
        <div className="d-inline-table">
          <div className="d-flex align-items-center">
            <Button className="p-button p-button-secondary outline me-2" label="Accept" onClick={() => approveIntake(rowData)} />
            <a
              className="text-decoration-none pointer tooltip-intake danger-text"
              data-pr-tooltip="Delete"
              onClick={() => rejectIntake(rowData)}
            >
              <i className="icon-delete icon-red cursor-pointer ms-2" />
            </a>
          </div>
        </div>
      </>
    );
  };
  const renderFormName = (row, { header }) => (
    <>
      <span className="p-column-title text-break">{header}</span>
      <div className="d-inline-table">
        <a className="text-decoration-none pointer call-back" onClick={() => generateFormPdf(row)}>
          {row?.form_name}
        </a>
      </div>
    </>
  );

  const renderLastModified = (row, { field, header }) => (
    <>
      <span className="p-column-title text-break">{header}</span>
      <div className="d-inline-table intake-dates">
        <small>{handleDateTimeOffset(userContext?.userDetails?.timezone, getPropByString(row, field))}</small>
      </div>
    </>
  );
  return (
    <div className="col-12 p-0 datatable-responsive">
      <DataTable
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={expandedRowTemplate}
        value={leadForms || []}
        className="p-datatable-responsive"
        responsiveLayout="scroll"
        emptyMessage="No Data Found."
        paginator
        rowHover
        paginatorTemplate={paginatorTemplate}
        rows={pageSize}
        paginatorClassName="mt-1"
      >
        <Column expander style={{ width: '5rem' }} />
        <Column header="Name" field="nickname" body={nameTemplate} />
        <Column header="Date Added" field="time" body={renderLastModified} />
        <Column header="Submitted Form" field="form_name" body={renderFormName} />
        <Column header="Actions" body={actionBody} />
      </DataTable>
    </div>
  );
}
